/** @jsx jsx */
import {
  Box,
  Button,
  CardHorizontalDivider,
  Container,
  Flex,
  FullScreenModal,
  FullScreenModalContainer,
  FullScreenModalHeader,
  FullScreenModalHeading,
  FullScreenModalProps,
  Heading,
  InfoBox,
  Paragraph,
  PopoverDisclosure,
  usePopover,
} from "@eworkit/iris";
import React, { FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { CollapsibleArea, CollapsibleButton, useCollapsible } from "@/components/collapsible";
import { FullScreenModalFloatingActions } from "@/components/dialogs";
import { UserPopover } from "@/components/popovers/UserPopover";
import { formatFullName } from "@/data/persons";
import { TimeReport, useTimeReportAggregate } from "@/data/time-reports";

import { TimeReportHeader } from "../header";
import { TimeReportSpecification } from "../specification";
import { TimeReportSummaryProjects } from "../summary";

interface PreviewTimeReportModalProps extends FullScreenModalProps {
  timeReport: TimeReport;
  onEditClick: () => void;
  onSubmitClick: () => void;
}

export const PreviewTimeReportModal: FC<PreviewTimeReportModalProps> = ({
  timeReport,
  onEditClick,
  onSubmitClick,
  ...modalProps
}) => {
  const { t } = useTranslation("session");

  const specificationCollapsibleState = useCollapsible(false);

  const specificationAggregateData = useTimeReportAggregate(timeReport, {
    from: new Date(timeReport.startDate),
    to: new Date(timeReport.endDate),
    filterEmptyItems: true,
  });

  const popoverState = usePopover();

  return (
    <FullScreenModal {...modalProps} aria-label={t("TIME_REPORTS.PREVIEW.PREVIEW_TIME_REPORT")}>
      <FullScreenModalContainer>
        <FullScreenModalHeader
          onCloseClick={modalProps.dialogState.hide}
          closeButtonAriaLabel={t("COMPONENTS.MODAL.CLOSE")}>
          <FullScreenModalHeading>{t("TIME_REPORTS.PREVIEW.PREVIEW_TIME_REPORT")}</FullScreenModalHeading>
        </FullScreenModalHeader>

        <Flex flexDirection="column">
          <Box py="md" borderBottom="primary">
            <Container>
              <Box px="sm">
                <TimeReportHeader timeReport={timeReport} />
              </Box>
            </Container>
          </Box>

          <Container>
            <Box px="sm" py="md">
              {typeof timeReport.remainingSpendPercentage === "number" && timeReport.remainingSpendPercentage < 0.1 && (
                <Box mb="sm">
                  <InfoBox
                    component={
                      <React.Fragment>
                        <Paragraph>
                          <Trans
                            ns="session"
                            i18nKey={
                              timeReport.remainingSpendPercentage < 0
                                ? "TIME_REPORTS.PREVIEW.BUDGET_EXCEEDED"
                                : "TIME_REPORTS.PREVIEW.BUDGET_LESS_THAN_10_PERCENT"
                            }
                            values={{ hiringManager: formatFullName(timeReport.hiringManager) }}>
                            <PopoverDisclosure as={Paragraph} bold {...popoverState} />
                          </Trans>
                        </Paragraph>

                        <UserPopover user={timeReport.hiringManager} state={popoverState} />
                      </React.Fragment>
                    }
                    variant={timeReport.remainingSpendPercentage < 0 ? "danger" : "information"}
                  />
                </Box>
              )}

              <Flex flexDirection="column" gap="xs">
                <TimeReportSummaryProjects timeReport={timeReport} headingSize="h-md" dividerSize="normal" gap="md" />

                {!specificationAggregateData.aggregateTotalReportedTime.isEmpty && (
                  <React.Fragment>
                    <CardHorizontalDivider />

                    <Flex flexDirection="column" gap="md">
                      <Flex alignItems="center" justifyContent="space-between">
                        <Heading size="h-md">{t("TIME_REPORTS.PREVIEW.SPECIFICATION")}</Heading>
                        <CollapsibleButton {...specificationCollapsibleState} />
                      </Flex>
                      <CollapsibleArea {...specificationCollapsibleState}>
                        <TimeReportSpecification timeReport={timeReport} aggregateData={specificationAggregateData} />
                      </CollapsibleArea>
                    </Flex>
                  </React.Fragment>
                )}
              </Flex>
            </Box>
          </Container>

          <FullScreenModalFloatingActions>
            <Button variant="ghost" onClick={modalProps.dialogState.hide}>
              {t("TIME_REPORTS.PREVIEW.CLOSE")}
            </Button>
            <Button variant="secondary" onClick={onEditClick}>
              {t("TIME_REPORTS.PREVIEW.EDIT")}
            </Button>
            <Button
              onClick={onSubmitClick}
              disabled={
                typeof timeReport.remainingSpendPercentage === "number" && timeReport.remainingSpendPercentage < 0
              }>
              {t("TIME_REPORTS.PREVIEW.SUBMIT_TIME_REPORT")}
            </Button>
          </FullScreenModalFloatingActions>
        </Flex>
      </FullScreenModalContainer>
    </FullScreenModal>
  );
};
